import React from 'react';
import type { FileSystem } from './types';

export const INITIAL_DATA: FileSystem = [
  {
    id: 'folder-1',
    name: 'Marketing Copy',
    prompts: [
      {
        id: 'prompt-1-1',
        name: 'Ad Headline Generator',
        content: 'Generate 5 catchy headlines for a new brand of coffee called "Morning Rocket". The target audience is young professionals. The tone should be energetic and witty.',
        tags: ['copywriting', 'ads', 'social-media'],
        isFavorite: true,
        updatedAt: '2024-07-29T10:00:00Z',
        versions: [],
        useCount: 0,
        lastUsed: null,
        deletedAt: null,
      },
      {
        id: 'prompt-1-2',
        name: 'SEO Blog Post Idea',
        content: 'Brainstorm 10 blog post ideas about sustainable fashion for a Fall 2024 campaign. Focus on keywords like "eco-friendly", "autumn style", and "conscious consumerism".',
        tags: ['seo', 'blogging', 'fashion'],
        isFavorite: false,
        updatedAt: '2024-07-28T14:20:00Z',
        versions: [],
        useCount: 0,
        lastUsed: null,
        deletedAt: null,
      },
    ],
    subFolders: [
      {
        id: 'folder-1-1',
        name: 'Email Campaigns',
        prompts: [
          {
            id: 'prompt-1-1-1',
            name: 'Welcome Email Series',
            content: 'Write a 3-part email series for new subscribers. Part 1: Welcome and offer a 10% discount. Part 2: Tell our brand story. Part 3: Showcase our best-selling products.',
            tags: ['email', 'drip-campaign'],
            isFavorite: false,
            updatedAt: '2024-07-27T11:00:00Z',
            versions: [],
            useCount: 0,
            lastUsed: null,
            deletedAt: null,
          },
        ],
        subFolders: [],
      },
    ],
  },
  {
    id: 'folder-2',
    name: 'Development',
    prompts: [
      {
        id: 'prompt-2-1',
        name: 'React Component from Description',
        content: 'Create a functional React component in TypeScript using Tailwind CSS for a user profile card. It should include an avatar, name, username, and a "Follow" button. State should be managed for the follow button (Follow/Following).',
        tags: ['react', 'typescript', 'frontend'],
        isFavorite: true,
        updatedAt: '2024-07-29T12:45:00Z',
        versions: [],
        useCount: 0,
        lastUsed: null,
        deletedAt: null,
      },
       {
        id: 'prompt-2-2',
        name: 'SQL Query Generator',
        content: 'Write a SQL query to select all users who have signed up in the last 30 days and have made at least one purchase. The tables are `users` (id, name, signup_date) and `purchases` (id, user_id, amount, purchase_date).',
        tags: ['sql', 'database', 'backend'],
        isFavorite: false,
        updatedAt: '2024-07-25T09:00:00Z',
        versions: [],
        useCount: 0,
        lastUsed: null,
        deletedAt: null,
      },
    ],
    subFolders: [],
  },
];

// ========================================
// Symbolsatz „Linienplan“
// Ein Raster (24 px), eine Strichstärke (1,75), runde Enden.
// Jede Funktion hat genau ein Symbol, kein Symbol steht für zwei Dinge:
// Stern = Favorit, Funke = KI, Kolben = Testen, Stab = Verbessern,
// Ebenen = Versionen, Stempel = Vorlage, Uhr = Zuletzt verwendet.
// ========================================

type IconProps = React.SVGProps<SVGSVGElement>;

const BASE_CLASS = 'h-5 w-5 inline-block shrink-0 pointer-events-none';

const Icon: React.FC<IconProps & { defaultClassName?: string }> = ({ defaultClassName = BASE_CLASS, className, children, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
    className={className ?? defaultClassName}
  >
    {children}
  </svg>
);

const SMALL = 'h-4 w-4 inline-block shrink-0 pointer-events-none';

// ----- Ablage -----
const FOLDER_PATH = 'M3 7.5A2.5 2.5 0 0 1 5.5 5h3.4a1.5 1.5 0 0 1 1.06.44L11.5 7h7A2.5 2.5 0 0 1 21 9.5v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z';
export const FolderIcon = (props: IconProps) => <Icon {...props}><path d={FOLDER_PATH} /></Icon>;
/** Ordner mit Pfeil: in anderen Ordner verschieben */
export const FolderMoveIcon = (props: IconProps) => <Icon {...props}><path d={FOLDER_PATH} /><path d="M9 13.5h6.5M13 11l2.5 2.5L13 16" /></Icon>;
export const FileTextIcon = (props: IconProps) => <Icon {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></Icon>;
export const FilePlusIcon = (props: IconProps) => <Icon {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M12 11.5v6M9 14.5h6" /></Icon>;
export const TrashIcon = (props: IconProps) => <Icon {...props}><path d="M4 6.5h16M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5" /><path d="M6 6.5l.8 12.1A2 2 0 0 0 8.8 20.5h6.4a2 2 0 0 0 2-1.9L18 6.5" /><path d="M10 10.5v6M14 10.5v6" /></Icon>;
/** Pfeil zurück aus der Ablage: wiederherstellen, Version laden */
export const RestoreIcon = (props: IconProps) => <Icon {...props}><path d="M8.5 13.5 4 9l4.5-4.5" /><path d="M4 9h10a5.5 5.5 0 0 1 0 11h-3.5" /></Icon>;
export const DownloadIcon = (props: IconProps) => <Icon {...props}><path d="M12 3.5v11M7.5 10l4.5 4.5 4.5-4.5" /><path d="M4 15.5v2A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5v-2" /></Icon>;
export const UploadIcon = (props: IconProps) => <Icon {...props}><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5" /><path d="M4 15.5v2A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5v-2" /></Icon>;
export const SaveIcon = (props: IconProps) => <Icon {...props}><path d="M5.5 3.5h10l5 5v10a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2z" /><path d="M7.5 3.5v4h7v-4M7.5 20.5v-6h9v6" /></Icon>;
export const DatabaseIcon = (props: IconProps) => <Icon {...props}><ellipse cx="12" cy="5.5" rx="8" ry="2.5" /><path d="M4 5.5v13c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-13M4 12c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5" /></Icon>;

// ----- Kopieren und Vorlagen -----
/** Zwei Blätter: kopieren */
export const ClipboardCopyIcon = (props: IconProps) => <Icon {...props}><rect x="8.5" y="8.5" width="12" height="12" rx="2.5" /><path d="M15.5 8.5V6A2.5 2.5 0 0 0 13 3.5H6A2.5 2.5 0 0 0 3.5 6v7A2.5 2.5 0 0 0 6 15.5h2.5" /></Icon>;
/** Zwei Blätter mit Plus: duplizieren */
export const CopyPlusIcon = (props: IconProps) => <Icon {...props}><rect x="8.5" y="8.5" width="12" height="12" rx="2.5" /><path d="M15.5 8.5V6A2.5 2.5 0 0 0 13 3.5H6A2.5 2.5 0 0 0 3.5 6v7A2.5 2.5 0 0 0 6 15.5h2.5" /><path d="M14.5 11.5v6M11.5 14.5h6" /></Icon>;
/** Stempel: Vorlage */
export const StampIcon = (props: IconProps) => <Icon {...props}><path d="M12 3.5a2.75 2.75 0 0 0-2.75 2.75c0 1.6 1.25 2.4 1.25 4.25v1H7a2.5 2.5 0 0 0-2.5 2.5v1.5h15V14a2.5 2.5 0 0 0-2.5-2.5h-3.5v-1c0-1.85 1.25-2.65 1.25-4.25A2.75 2.75 0 0 0 12 3.5z" /><path d="M5.5 20h13" /></Icon>;
export const ClipboardListIcon = StampIcon;
export const TranslateIcon = (props: IconProps) => <Icon {...props}><path d="m5 8 6 6M4 14l6-6 2-3M2.5 5h11M7.5 2.5h1" /><path d="m21.5 21.5-5-10-5 10M13.5 17.5h6" /></Icon>;

// ----- Verlauf und Versionen -----
/** Uhr mit Rücklauf: zuletzt verwendet */
export const HistoryIcon = (props: IconProps) => <Icon {...props}><path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" /><path d="M3.5 4v4h4" /><path d="M12 7.5V12l3 2" /></Icon>;
/** Gestapelte Ebenen: Versionen */
export const LayersIcon = (props: IconProps) => <Icon {...props}><path d="M12 3.5 20.5 8 12 12.5 3.5 8z" /><path d="m3.5 12 8.5 4.5 8.5-4.5" /><path d="m3.5 16 8.5 4.5 8.5-4.5" /></Icon>;
/** Ebenen mit Plus: als Version sichern */
export const LayersPlusIcon = (props: IconProps) => <Icon {...props}><path d="M11 3.5 19 8l-8 4.5L3 8z" /><path d="m3 12 8 4.5 2.5-1.4" /><path d="m3 16 8 4.5 2.5-1.4" /><path d="M18.5 13.5v7M15 17h7" /></Icon>;

// ----- KI-Funktionen: alle tragen den Funken -----
/** Vierstrahliger Funke: KI-Vorschlag */
export const SparklesIcon = (props: IconProps) => <Icon {...props}><path d="M10 3.5c.45 4.1 1.9 5.55 6 6-4.1.45-5.55 1.9-6 6-.45-4.1-1.9-5.55-6-6 4.1-.45 5.55-1.9 6-6z" /><path d="M18 14.5c.2 1.85.9 2.55 2.75 2.75-1.85.2-2.55.9-2.75 2.75-.2-1.85-.9-2.55-2.75-2.75 1.85-.2 2.55-.9 2.75-2.75z" /></Icon>;
/** Zauberstab mit Funken: Prompt verbessern */
export const WandIcon = (props: IconProps) => <Icon {...props}><path d="m3.5 20.5 11-11" /><path d="m13 8 3 3" /><path d="M17.5 3v3.5M15.75 4.75h3.5" /><path d="M20 10.5v2.5M18.75 11.75h2.5" /><path d="M9 3.5v2M8 4.5h2" /></Icon>;
export const Wand2Icon = WandIcon;
/** Sprechblase mit Funke: Prompt-Generator im Dialog */
export const ChatSparkIcon = (props: IconProps) => <Icon {...props}><path d="M6 4h12a2.5 2.5 0 0 1 2.5 2.5v8A2.5 2.5 0 0 1 18 17h-7l-4.5 3.5V17H6a2.5 2.5 0 0 1-2.5-2.5v-8A2.5 2.5 0 0 1 6 4z" /><path d="M12 7c.25 1.95.8 2.5 2.75 2.75-1.95.25-2.5.8-2.75 2.75-.25-1.95-.8-2.5-2.75-2.75C11.2 9.5 11.75 8.95 12 7z" /></Icon>;
/** Erlenmeyerkolben: mit KI testen */
export const FlaskIcon = (props: IconProps) => <Icon {...props}><path d="M9 3.5h6M10 3.5v5.8L4.7 18a2 2 0 0 0 1.7 3h11.2a2 2 0 0 0 1.7-3L14 9.3V3.5" /><path d="M7.2 15h9.6" /></Icon>;
/** Chip: KI-Modell, Anbieter, API-Schlüssel */
export const BrainCircuitIcon = (props: IconProps) => <Icon {...props}><rect x="6.5" y="6.5" width="11" height="11" rx="2" /><path d="M10 10h4v4h-4z" /><path d="M9.5 3v3.5M14.5 3v3.5M9.5 17.5V21M14.5 17.5V21M3 9.5h3.5M3 14.5h3.5M17.5 9.5H21M17.5 14.5H21" /></Icon>;
export const ChipIcon = BrainCircuitIcon;
export const LightbulbIcon = (props: IconProps) => <Icon {...props}><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" /><path d="M9 18h6M10 21.5h4" /></Icon>;

// ----- Bewerten und Ordnen -----
export const StarIcon: React.FC<{ filled?: boolean; className?: string }> = ({ filled, className }) => (
  <Icon className={className} fill={filled ? 'currentColor' : 'none'}>
    <path d="M12 3.2l2.6 5.3 5.9.86-4.25 4.15 1 5.85L12 16.6l-5.25 2.76 1-5.85L3.5 9.36l5.9-.86z" />
  </Icon>
);
export const TagIcon = (props: IconProps) => <Icon {...props}><path d="M3.5 5.5v5.17a2 2 0 0 0 .59 1.42l7.32 7.32a2 2 0 0 0 2.83 0l5.17-5.17a2 2 0 0 0 0-2.83L12.09 4.09a2 2 0 0 0-1.42-.59H5.5a2 2 0 0 0-2 2z" /><circle cx="8.25" cy="8.25" r="1.25" fill="currentColor" stroke="none" /></Icon>;
export const SortIcon = (props: IconProps) => <Icon defaultClassName={SMALL} {...props}><path d="m3.5 16 3.5 3.5 3.5-3.5M7 19.5v-15" /><path d="m20.5 8-3.5-3.5L13.5 8M17 4.5v15" /></Icon>;
export const SearchIcon = (props: IconProps) => <Icon defaultClassName={SMALL} {...props}><circle cx="11" cy="11" r="7" /><path d="m20.5 20.5-4.55-4.55" /></Icon>;

// ----- Bearbeiten -----
export const PencilIcon = (props: IconProps) => <Icon {...props}><path d="M15.5 4.5a2.12 2.12 0 0 1 3 3L8 18l-4.5 1.5L5 15z" /><path d="m13.5 6.5 3 3" /></Icon>;
export const EyeIcon = (props: IconProps) => <Icon {...props}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /></Icon>;
export const PlusIcon = (props: IconProps) => <Icon {...props}><path d="M12 5v14M5 12h14" /></Icon>;
export const XIcon = (props: IconProps) => <Icon {...props}><path d="M18 6 6 18M6 6l12 12" /></Icon>;
export const CheckIcon = (props: IconProps) => <Icon {...props}><path d="m4.5 12.5 5 5 10-11" /></Icon>;
export const SendIcon = (props: IconProps) => <Icon {...props}><path d="M21 3 10.5 13.5" /><path d="m21 3-6.5 18-4-7.5-7.5-4z" /></Icon>;
export const LinkIcon = (props: IconProps) => <Icon {...props}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Icon>;
export const Loader2Icon = (props: IconProps) => <Icon defaultClassName="h-5 w-5 animate-spin" {...props}><path d="M21 12a9 9 0 1 1-6.22-8.56" /></Icon>;

// ----- Navigation -----
export const MenuIcon = (props: IconProps) => <Icon {...props}><path d="M4 6.5h16M4 12h16M4 17.5h16" /></Icon>;
export const MoreVerticalIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="5.5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
  </Icon>
);
export const ChevronsLeftIcon = (props: IconProps) => <Icon {...props}><path d="m11 17-5-5 5-5M18 17l-5-5 5-5" /></Icon>;
export const ChevronRightIcon = (props: IconProps) => <Icon defaultClassName={SMALL} {...props}><path d="m9 18 6-6-6-6" /></Icon>;
export const ChevronDownIcon = (props: IconProps) => <Icon defaultClassName={SMALL} {...props}><path d="m6 9 6 6 6-6" /></Icon>;
export const ChevronUpIcon = (props: IconProps) => <Icon defaultClassName={SMALL} {...props}><path d="m18 15-6-6-6 6" /></Icon>;
export const ArrowLeftIcon = (props: IconProps) => <Icon {...props}><path d="M19 12H5M11.5 18.5 5 12l6.5-6.5" /></Icon>;
export const ArrowUpIcon = (props: IconProps) => <Icon {...props}><path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" /></Icon>;
export const ArrowDownIcon = (props: IconProps) => <Icon {...props}><path d="M12 5v14M18.5 12.5 12 19l-6.5-6.5" /></Icon>;

// ----- Konto, Verwaltung, Darstellung -----
export const SunIcon = (props: IconProps) => <Icon {...props}><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" /></Icon>;
export const MoonIcon = (props: IconProps) => <Icon {...props}><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" /></Icon>;
/** Balken: Statistik */
export const ChartIcon = (props: IconProps) => <Icon {...props}><path d="M3.5 20.5h17" /><path d="M6.5 20.5v-7M11 20.5V6M15.5 20.5v-10M20 20.5v-4" /></Icon>;
/** Tastatur: Tastenkürzel */
export const KeyboardIcon = (props: IconProps) => <Icon {...props}><rect x="2.5" y="5.5" width="19" height="13" rx="2.5" /><path d="M6.5 9.5h.01M10 9.5h.01M14 9.5h.01M17.5 9.5h.01M6.5 12.5h.01M17.5 12.5h.01M10 12.5h4M8 15.5h8" /></Icon>;
/** Schieberegler: Einstellungen */
export const SettingsIcon = (props: IconProps) => <Icon {...props}><path d="M4 7h8M16 7h4M4 12h3M11 12h9M4 17h10M18 17h2" /><circle cx="14" cy="7" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="16" cy="17" r="2" /></Icon>;
export const SlidersIcon = SettingsIcon;
/** Person mit Schild: Benutzerverwaltung */
export const AdminIcon = (props: IconProps) => <Icon {...props}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 10-5.5" /><path d="M18 12.5l3.5 1.4v2.4c0 2.1-1.5 3.6-3.5 4.2-2-.6-3.5-2.1-3.5-4.2v-2.4z" /></Icon>;
export const ShieldCheckIcon = (props: IconProps) => <Icon {...props}><path d="M12 21.5s7.5-3.5 7.5-9.5V5.5L12 2.5l-7.5 3V12c0 6 7.5 9.5 7.5 9.5z" /><path d="m9 12 2.2 2.2L15.5 10" /></Icon>;
export const ShieldCogIcon = AdminIcon;
export const UserIcon = (props: IconProps) => <Icon {...props}><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" /></Icon>;
export const LockIcon = (props: IconProps) => <Icon {...props}><rect x="4" y="10.5" width="16" height="10.5" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></Icon>;
// Schlüssel = Zugangsschlüssel für Skripte (Skill-Sync)
export const KeyIcon = (props: IconProps) => <Icon {...props}><circle cx="7.5" cy="15.5" r="4" /><path d="m10.5 12.5 9-9M16 7l2.5 2.5M13.5 9.5 15.5 11.5" /></Icon>;
export const UnlockIcon = (props: IconProps) => <Icon {...props}><rect x="4" y="10.5" width="16" height="10.5" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 7.75-1.4" /></Icon>;
export const LogOutIcon = (props: IconProps) => <Icon {...props}><path d="M9.5 20.5H6A2.5 2.5 0 0 1 3.5 18V6A2.5 2.5 0 0 1 6 3.5h3.5" /><path d="m15.5 16.5 4.5-4.5-4.5-4.5M20 12H9.5" /></Icon>;

// ----- Linienschilder der Eintragsarten -----
// Jede Art hat Farbe UND Form, damit sie auch ohne Farbsehen unterscheidbar bleibt:
// Prompt = Quadrat mit Eingabezeichen ›_, Skill = Sechseck (Baustein),
// Workflow = Kreis (Station) mit Streckenverlauf.
// Die Fläche übernimmt currentColor, das Zeichen die Panel-Farbe.
export const KindGlyph: React.FC<{ kind: 'prompt' | 'skill' | 'workflow' | 'gem'; className?: string }> = ({ kind, className }) => {
  const cls = className ?? 'h-4 w-4 inline-block shrink-0 pointer-events-none';
  const mark = 'rgb(var(--sheet))';
  if (kind === 'skill') {
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 1.8 21 7v10l-9 5.2L3 17V7z" fill="currentColor" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3.4" fill="none" stroke={mark} strokeWidth={2.4} />
      </svg>
    );
  }
  if (kind === 'gem') {
    // Geschliffener Stein: Krone oben, Spitze unten
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6.2 2.5h11.6L22.5 9 12 22 1.5 9z" fill="currentColor" stroke="currentColor" strokeWidth={1.2} strokeLinejoin="round" />
        <path d="M4.2 9h15.6M9 9l3 8.5L15 9" fill="none" stroke={mark} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'workflow') {
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="10.5" fill="currentColor" />
        <path d="M8 8v2.25A2 2 0 0 0 10 12.25h4A2 2 0 0 1 16 14.25V16" fill="none" stroke={mark} strokeWidth={2.2} strokeLinecap="round" />
        <circle cx="8" cy="7.5" r="1.9" fill={mark} />
        <circle cx="16" cy="16.5" r="1.9" fill={mark} />
      </svg>
    );
  }
  return (
    <svg className={cls} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1.75" y="1.75" width="20.5" height="20.5" rx="5" fill="currentColor" />
      <path d="m7 8 4 4-4 4M12.5 16.5H17" fill="none" stroke={mark} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
